import {
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { useQueryPayerEngagement } from "../../../../query/payerEngagements/queries/useQueryPayerEngagement";
import { useQueryPayer } from "../../../../query/payers/queries/useQueryPayer";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getPayerName } from "../../../Payers/utils/getPayerNames";
import { AlertWorkLogInvoiceStatusPayee } from "../../components/AlertWorkLogInvoiceStatusPayee";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetailsEditable } from "../../components/WorkLogAdditionalDetailsEditable";
import { WorkLogAssignmentInfoPayeeView } from "../../components/WorkLogAssignmentInfoPayeeView";
import { WorkLogStatusStepperPayeeView } from "../../components/WorkLogStatusStepperPayeeView";
import { useModalConvert } from "../../modals/ModalConvert";
import { getPathWorkLogEdit, PATH_WORK_LOGS_PAYEE } from "../../paths";

/**
 * Check if payee can edit the work log
 * Payee can edit work logs in Draft or InProgress status
 */
function canPayeeEditWorkLog(status?: WorkLogStatus): boolean {
  if (!status) return false;
  // TODO: Add WorkLogStatus.InProgress when @wingspanhq/payments adds it
  return status === WorkLogStatus.Draft;
}

/**
 * Check if payee can submit the work log for review
 * Payee can submit work logs in Draft or InProgress status
 */
function canPayeeSubmitWorkLog(status?: WorkLogStatus): boolean {
  if (!status) return false;
  // TODO: Add WorkLogStatus.InProgress when @wingspanhq/payments adds it
  return status === WorkLogStatus.Draft;
}

/**
 * Check if payee can delete the work log
 * Payee can delete work logs in Draft status only
 */
function canPayeeDeleteWorkLog(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return status === WorkLogStatus.Draft;
}

export const RoutePayeeWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const userId = useUserId();
  const history = useHistory();

  // Query work log
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  // Query payer information
  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayer = useQueryPayer(payerId!, {
    enabled: !!payerId
  });

  const queryPayerPayeeEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query work definitions
  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  // Query invoicing config
  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  // Query work items
  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  // Modal for converting to invoice
  const modalConvert = useModalConvert();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Work Log Details";
    }

    if (!payer) {
      return "Work Log Details";
    }

    const payerName = getPayerName(payer);

    if (payerPayeeEngagement) {
      return `Work Log – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Work Log – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Feature flags
  const queryFeatureFlags = useFeatureFlags();
  const isWorkLogReviewWorkflow = queryFeatureFlags.data?.workLogReviewWorkflow;

  // Derived state for work log status
  const canEdit = canPayeeEditWorkLog(workLog?.status);
  const canSubmit = canPayeeSubmitWorkLog(workLog?.status);
  const canDelete = canPayeeDeleteWorkLog(workLog?.status);
  const isApproved = workLog?.status === WorkLogStatus.Approved;
  const isDraft = workLog?.status === WorkLogStatus.Draft;

  // Action handlers
  const handleViewInvoice = useCallback(() => {
    history.push(`/member/invoices/${workLog?.invoiceId}`);
  }, [workLog?.invoiceId, history]);

  const handleCreateInvoice = useCallback(() => {
    modalConvert.open({
      workLogId,
      onClose: result => {
        if (result && !result.createError) {
          queryWorkLog.refetch();
        }
      }
    });
  }, [modalConvert, workLogId, queryWorkLog]);

  const handleEditWorkLog = useCallback(() => {
    history.push(getPathWorkLogEdit(workLogId));
  }, [history, workLogId]);

  const handleSubmitForReview = useCallback(() => {
    // TODO: Implement submit for review mutation when backend is ready
    // This will call the API to transition work log from Draft/InProgress to InReview
    console.log("Submit for review:", workLogId);
  }, [workLogId]);

  const handleDeleteWorkLog = useCallback(() => {
    // TODO: Implement delete work log modal when backend is ready
    // This will open a confirmation modal before deleting the work log
    console.log("Delete work log:", workLogId);
  }, [workLogId]);

  // Build breadcrumbs following route structure: Work logs -> Work log #
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const breadcrumbs = useMemo(
    () => [
      {
        label: "Work logs",
        onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
      },
      {
        label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
      }
    ],
    [history, workLog]
  );

  // Build toolbar actions based on work log status
  const toolbarActions = useMemo(() => {
    // New experience with workflow actions
    if (isWorkLogReviewWorkflow) {
      const actions: WSPageToolbarAction[] = [];

      // View invoice button - shown when work log is approved with invoice
      if (isApproved && workLog?.invoiceId) {
        actions.push({
          label: "View invoice",
          buttonKind: "Primary",
          onClick: handleViewInvoice,
          buttonOrder: 1
        });
      }

      // Menu items for payee actions (shown in three-dot menu)
      // Edit work log
      if (canEdit) {
        actions.push({
          label: "Edit work log",
          icon: "edit",
          onClick: handleEditWorkLog
        });
      }

      // Submit for review
      if (canSubmit) {
        actions.push({
          label: "Submit for review",
          icon: "send",
          onClick: handleSubmitForReview
        });
      }

      // Delete work log (destructive)
      if (canDelete) {
        actions.push({
          label: "Delete work log",
          icon: "trash",
          onClick: handleDeleteWorkLog,
          destructive: true
        });
      }

      return actions;
    }

    // Legacy experience - view/create invoice
    const legacyActions: WSPageToolbarAction[] = [
      {
        label: "View invoice",
        buttonKind: "Primary",
        onClick: handleViewInvoice,
        hidden: isDraft
      },
      {
        label: "Create invoice",
        buttonKind: "Primary",
        onClick: handleCreateInvoice,
        disabled: (queryWorkItems.data || []).length === 0,
        hidden: isApproved
      }
    ];
    return legacyActions;
  }, [
    isWorkLogReviewWorkflow,
    isApproved,
    isDraft,
    workLog?.invoiceId,
    canEdit,
    canSubmit,
    canDelete,
    handleViewInvoice,
    handleEditWorkLog,
    handleSubmitForReview,
    handleDeleteWorkLog,
    handleCreateInvoice,
    queryWorkItems.data
  ]);

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          type="search"
          title="Work log not found"
          description="The work log you're looking for doesn't exist or has been deleted."
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
              }
            ]
          }}
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            type="error"
            title="Access denied"
            description="You don't have permission to view this work log."
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
                }
              ]
            }}
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
        breadcrumbs={breadcrumbs}
        actions={toolbarActions}
      />

      <AlertWorkLogInvoiceStatusPayee workLog={workLog} />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayeeView
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
              payer={payer}
              payerPayeeEngagement={payerPayeeEngagement}
            />

            <CardWorkItems
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryWorkDefinitions.isInitialLoading
              }
              noEdit={workLog?.status === WorkLogStatus.Approved}
              workLogId={workLogId}
              payerId={payerId!}
              payerPayeeEngagementId={workLog?.payerPayeeEngagementId!}
              workItems={queryWorkItems.data || []}
              workDefinitions={queryWorkDefinitions.data || []}
              resources={{ payerPayeeEngagement }}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogAdditionalDetailsEditable
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
              payerId={payerId}
              payeeId={userId}
            />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
